import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, ListChecks, UserPlus } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AccountClient, PasswordClient, ResourceClient, type AccountDTO } from "../../api";
import { useAuth } from "../../auth";
import { columns as defineColumns, DataTable, type RowEditorConfig } from "../../table";
import { Badge, Button, Dialog, Field, Input } from "../../ui";
import { PageHeader } from "../PageHeader";
import { ViewPickerDialog } from "./ViewPickerDialog";

interface NewUser {
  username: string;
  email: string;
  password: string;
  level: AccountDTO["level"];
}

const BLANK: NewUser = { username: "", email: "", password: "", level: "USER" };

/**
 * User administration, shared by all three apps.
 *
 * One constraint shapes this whole screen: `AccountRepository.update` writes
 * the password column unconditionally, so any DTO that reaches it without the
 * stored hash wipes that account's credential. Every write path here therefore
 * either round-trips the hash untouched or goes through the dedicated
 * password endpoint — never both, and never neither.
 */
export function UsersPage() {
  const { t } = useTranslation();
  const { http, account } = useAuth();
  const queryClient = useQueryClient();

  // Matches the Vaadin view's @RolesAllowed("ADMIN"). The endpoint itself
  // allows any authenticated GET, so without this a USER-level account could
  // reach the account list by typing the URL.
  const allowed = account?.level === "ROOT" || account?.level === "ADMIN";

  const client = useMemo(() => new ResourceClient<AccountDTO>(http, "/account"), [http]);
  const passwords = useMemo(() => new PasswordClient(http), [http]);
  const accounts = useMemo(() => new AccountClient(http), [http]);

  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<NewUser>(BLANK);
  const [resetting, setResetting] = useState<AccountDTO | null>(null);
  const [editingViews, setEditingViews] = useState<AccountDTO | null>(null);
  const [newPassword, setNewPassword] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["account", "all"],
    queryFn: () => client.findAll(),
    enabled: allowed,
  });

  // The app tells us which views exist; the frontend no longer keeps a copy.
  const { data: views, isLoading: viewsLoading } = useQuery({
    queryKey: ["account", "views"],
    queryFn: () => accounts.views(),
    enabled: allowed,
    staleTime: 60 * 60_000,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["account", "all"] });

  /**
   * Creating a user is two calls: the row, then the credential. The account
   * DTO's password field carries a stored hash, so the plaintext can never
   * travel that way — it goes to the password endpoint, which hashes it.
   */
  const create = useMutation({
    mutationFn: async (user: NewUser) => {
      const created = await client.create({
        id: 0,
        level: user.level,
        status: "ACTIVE",
        credentials: { username: user.username.trim(), email: user.email.trim() },
        view_codes: [],
      } as AccountDTO);

      try {
        await passwords.change(created.id, { new_password: user.password });
      } catch (failure) {
        // Roll back rather than leave an account nobody can sign into.
        await client.remove(created.id).catch(() => undefined);
        throw failure;
      }
      return created;
    },
    onSuccess: async () => {
      setCreating(false);
      setDraft(BLANK);
      await refresh();
      toast.success(t("notification.saved", "Saved"));
    },
    onError: (failure) =>
      toast.error(failure instanceof Error ? failure.message : t("error.generic", "Failed")),
  });

  const resetPassword = useMutation({
    mutationFn: ({ id, password }: { id: number; password: string }) =>
      passwords.change(id, { new_password: password }),
    onSuccess: () => {
      setResetting(null);
      setNewPassword("");
      toast.success(t("user.password.changed", "Password changed"));
    },
    onError: (failure) =>
      toast.error(failure instanceof Error ? failure.message : t("error.generic", "Failed")),
  });

  const cols = useMemo(() => {
    const c = defineColumns<AccountDTO>();
    return [
      c.text("username", (row) => row.credentials.username, {
        headerKey: "column.username",
        priority: 1,
      }),
      c.text("email", (row) => row.credentials.email, { headerKey: "column.email", priority: 2 }),
      c.custom(
        "level",
        (row) => row.level,
        (row) => (
          <Badge
            variant={row.level === "ROOT" ? "error" : row.level === "ADMIN" ? "accent" : "neutral"}
            size="sm"
          >
            {row.level}
          </Badge>
        ),
        { headerKey: "column.level", filter: "select", priority: 3 },
      ),
      c.custom(
        "status",
        (row) => row.status,
        (row) => (
          <Badge variant={row.status === "ACTIVE" ? "success" : "warning"} size="sm">
            {row.status}
          </Badge>
        ),
        { headerKey: "column.status", filter: "select", priority: 3 },
      ),
      c.number("views", (row) => row.view_codes?.length ?? 0, {
        headerKey: "column.view-codes",
        priority: 4,
      }),
      c.text("logged_at", (row) => formatDate(row.logged_at), {
        headerKey: "column.last-login",
        priority: 4,
      }),
      c.text("created_at", (row) => formatDate(row.created_at), {
        headerKey: "column.created",
        defaultVisible: false,
      }),
    ];
  }, []);

  const editor = useMemo<RowEditorConfig<AccountDTO>>(
    () => ({
      fields: {
        username: {
          kind: "text",
          get: (row) => row.credentials.username,
          set: (row, value) => ({ ...row, credentials: { ...row.credentials, username: value } }),
          required: true,
        },
        email: {
          kind: "email",
          get: (row) => row.credentials.email,
          set: (row, value) => ({ ...row, credentials: { ...row.credentials, email: value } }),
          required: true,
        },
        level: {
          kind: "select",
          get: (row) => row.level,
          set: (row, value) => ({ ...row, level: value as AccountDTO["level"] }),
          options: [
            { value: "ROOT", label: "ROOT" },
            { value: "ADMIN", label: "ADMIN" },
            { value: "USER", label: "USER" },
          ],
          required: true,
        },
        status: {
          kind: "select",
          get: (row) => row.status,
          set: (row, value) => ({ ...row, status: value as AccountDTO["status"] }),
          options: [
            { value: "ACTIVE", label: "ACTIVE" },
            { value: "SUSPENDED", label: "SUSPENDED" },
          ],
          required: true,
        },
      },
      validate: (row): Record<string, string> => {
        const errors: Record<string, string> = {};
        if ((row.credentials.username ?? "").trim().length < 5) {
          errors.username = t("form.validation.field.length", "Value must be at least {{min}} characters", { min: 5 });
        }
        if (!row.credentials.email?.includes("@")) {
          errors.email = t("form.validation.field.invalid", "Invalid value");
        }
        return errors;
      },
      // `row` still carries the stored hash it was loaded with, and it must:
      // the repository writes the password column on every update, so dropping
      // the field here would blank the credential.
      onSave: async (row) => {
        await client.update(row);
        await refresh();
        toast.success(t("notification.saved", "Saved"));
      },
      onDelete: async (row) => {
        await client.remove(row.id);
        await refresh();
        toast.success(t("notification.deleted", "Deleted"));
      },
      deleteConfirm: (row) => ({
        title: t("dialog.delete.user.title", "Delete this user?"),
        message: row.credentials.username,
      }),
      rowActions: (row) => (
        <>
          <Button
            size="sm"
            variant="ghost"
            className="size-7 p-0"
            title={t("user.views.edit", "Edit page access")}
            aria-label={t("user.views.edit", "Edit page access")}
            onClick={() => setEditingViews(row)}
            icon={<ListChecks className="size-3.5" />}
          />
          <Button
            size="sm"
            variant="ghost"
            className="size-7 p-0"
            title={t("user.password.reset", "Set password")}
            aria-label={t("user.password.reset", "Set password")}
            onClick={() => {
              setNewPassword("");
              setResetting(row);
            }}
            icon={<KeyRound className="size-3.5" />}
          />
        </>
      ),
    }),
    [client, t, queryClient],
  );

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.users", "Users")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader title={t("navigation.item.users", "Users")} />
        <div className="p-4 lg:p-6">
          <p className="rounded-md bg-risk-soft px-3 py-2 text-[13px] text-risk">
            {t("error.generic", "Something went wrong. Please try again.")}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader title={t("navigation.item.users", "Users")} />

      <DataTable
        data={data ?? []}
        columns={cols}
        getRowId={(row) => String(row.id)}
        loading={isLoading}
        selectable
        editor={editor}
        actions={[
          {
            label: t("user.add", "Add user"),
            onClick: () => {
              setDraft(BLANK);
              setCreating(true);
            },
            icon: <UserPlus className="size-4" />,
            variant: "primary",
            write: true,
          },
        ]}
        onDeleteSelected={async (rows) => {
          await client.removeBatch(rows.map((row) => row.id));
          await refresh();
          toast.success(t("notification.deleted", "Deleted"));
        }}
        deleteSelectedConfirm={(count) => ({
          title: t("dialog.delete.users.title", "Delete {{count}} user(s)?", { count }),
        })}
        emptyMessage={t("table.empty", "Nothing to show")}
      />

      {/* A dialog rather than an inline add row: creation needs a password,
          which is not one of the table's columns and must not be. */}
      <Dialog
        open={creating}
        onClose={() => setCreating(false)}
        title={t("user.add", "Add user")}
        size="sm"
        actions={
          <>
            <Button variant="secondary" onClick={() => setCreating(false)}>
              {t("button.cancel", "Cancel")}
            </Button>
            <Button
              variant="primary"
              loading={create.isPending}
              disabled={
                draft.username.trim().length < 5 ||
                !draft.email.includes("@") ||
                draft.password.length < 6
              }
              onClick={() => create.mutate(draft)}
            >
              {t("button.add", "Add")}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Field label={t("column.username", "Username")} htmlFor="new-username" required>
            <Input
              id="new-username"
              autoFocus
              value={draft.username}
              onChange={(event) => setDraft({ ...draft, username: event.target.value })}
            />
          </Field>
          <Field label={t("column.email", "Email")} htmlFor="new-email" required>
            <Input
              id="new-email"
              type="email"
              value={draft.email}
              onChange={(event) => setDraft({ ...draft, email: event.target.value })}
            />
          </Field>
          <Field
            label={t("form.field.password", "Password")}
            htmlFor="new-password"
            required
            hint={t("user.password.hint", "At least 6 characters")}
          >
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={draft.password}
              onChange={(event) => setDraft({ ...draft, password: event.target.value })}
            />
          </Field>
          <Field label={t("column.level", "Level")} htmlFor="new-level">
            <select
              id="new-level"
              value={draft.level}
              onChange={(event) =>
                setDraft({ ...draft, level: event.target.value as AccountDTO["level"] })
              }
              className="h-9 w-full rounded-md border border-line bg-surface px-3 text-sm text-ink max-md:h-11"
            >
              <option value="USER">USER</option>
              <option value="ADMIN">ADMIN</option>
              <option value="ROOT">ROOT</option>
            </select>
          </Field>
        </div>
      </Dialog>

      <Dialog
        open={resetting !== null}
        onClose={() => setResetting(null)}
        title={t("user.password.reset", "Set password")}
        description={resetting?.credentials.username}
        size="sm"
        actions={
          <>
            <Button variant="secondary" onClick={() => setResetting(null)}>
              {t("button.cancel", "Cancel")}
            </Button>
            <Button
              variant="primary"
              loading={resetPassword.isPending}
              disabled={newPassword.length < 6 || !resetting}
              onClick={() =>
                resetting && resetPassword.mutate({ id: resetting.id, password: newPassword })
              }
            >
              {t("button.save", "Save")}
            </Button>
          </>
        }
      >
        <Field
          label={t("user.password.new", "New password")}
          htmlFor="reset-password"
          required
          hint={t("user.password.hint", "At least 6 characters")}
        >
          <Input
            id="reset-password"
            type="password"
            autoComplete="new-password"
            autoFocus
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
          />
        </Field>
      </Dialog>

      {editingViews && (
        <ViewPickerDialog
          open
          onClose={() => setEditingViews(null)}
          views={views ?? []}
          loading={viewsLoading}
          initial={editingViews.view_codes ?? []}
          username={editingViews.credentials.username}
          onSave={async (codes) => {
            // Spread the loaded row rather than building a fresh DTO: it still
            // carries the stored password hash, and the repository writes that
            // column on every update.
            await client.update({ ...editingViews, view_codes: codes });
            await refresh();
            setEditingViews(null);
            toast.success(t("notification.saved", "Saved"));
          }}
        />
      )}
    </>
  );
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toLocaleString();
}
